import React, { useEffect } from 'react';
import { Chip, Grid, TextField } from '@material-ui/core';
import {
  Dialog, DialogContent, DialogTitle, IconButton, Box,
  Fab, CircularProgress, FormControl, FormLabel, FormGroup, FormControlLabel, FormHelperText, Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import {
  Formik, Form, FormikProps, Field, ErrorMessage
} from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Autocomplete } from 'formik-material-ui-lab';
import { AutocompleteRenderInputParams } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import { ScaleLoader } from 'react-spinners';
import _ from 'lodash';

import { Create as Values } from '../../models/interfaces/customer';
import UseStyles from '../../components/styles/loadingButton';
import { setAddressByCEP } from '../../services/geolocateService';
import { CepMask, PhoneMask } from '../../components/mask';
import Dictionary from '../../models/interfaces/dictionary';
import subsidiaryService from '../../services/subsidiaryService';
import customerService from '../../services/customerService';
import { getErrorMessage } from '../../config/firebase';
import { CategoryDriverList } from '../../models/enums/categoryDriver';
import { PaymentTypeList } from '../../models/enums/paymentType';
import { useAuth } from '../../contexts/auth';
import driverService from '../../services/driverService';

interface Props {
  open: boolean;
  customerId: string | null;
  onClose: () => void;
  onSave: () => void;
}

// Função para obter valores iniciais
const getInitialValues = (customerData: any): Values => ({
  name: customerData?.name || '',
  businessName: customerData?.businessName || '',
  email: customerData?.email || '',
  registryNumber: customerData?.registryNumber || '',
  responsible: customerData?.responsible || '',
  contract: customerData?.contract || '',
  subsidiary: customerData?.subsidiary ? {
    key: customerData.subsidiary.id,
    value: customerData.subsidiary.description
  } : null,
  address: {
    zipCode: customerData?.address?.zipCode || '',
    street: customerData?.address?.street || '',
    number: customerData?.address?.number || '',
    complement: customerData?.address?.complement || '',
    neighborhood: customerData?.address?.neighborhood || '',
    city: customerData?.address?.city || '',
    state: customerData?.address?.state || null,
    ibge: customerData?.address?.ibge || null,
    location: customerData?.address?.location || null
  },
  phoneNumber: customerData?.phoneNumber || '',
  categories: customerData?.categories || [],
  paymentTypes: customerData?.paymentTypes || [],
  observationsToDriver: customerData?.observationsToDriver || '',
  observations: customerData?.observations || '',
  blockedDrivers: customerData?.blockedDrivers || []
});

export default function CustomerEditModal({
  open,
  customerId,
  onClose,
  onSave
}: Props) {
  const classes = UseStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [success, setSuccess] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [subsidiaries, setSubsidiaries] = React.useState<Dictionary[]>();
  const { subsidiaryId, user } = useAuth();

  const buttonClassname = clsx({ [classes.buttonSuccess]: success });

  const [drivers, setDrivers] = React.useState<Array<{ id: string, name: string }>>([]);
  const [searchDrivers, setSearchDrivers] = React.useState(''); // CORREÇÃO AQUI
  const [customerData, setCustomerData] = React.useState<any>(null);
  const [key, setKey] = React.useState(0); // Key para forçar re-render do Formik

  // Função para buscar motoristas
  const searchDriversForAutocomplete = async (searchTerm: string) => {
    if (searchTerm.length > 3) {
      try {
        const driversList = await driverService.getDriversForAutocomplete(subsidiaryId, searchTerm);
        setDrivers(driversList);
      } catch (error) {
        console.error('Erro ao buscar condutores:', error);
      }
    } else {
      setDrivers([]);
    }
  };

  // Debounce para a busca
  const debouncedSearchDrivers = React.useCallback(
    _.debounce(searchDriversForAutocomplete, 400),
    [subsidiaryId]
  );

  React.useEffect(() => {
    debouncedSearchDrivers(searchDrivers);
  }, [searchDrivers, debouncedSearchDrivers]);

  useEffect(() => {
    if (!subsidiaryId) {
      subsidiaryService.getByPaginationAsync(1, 200).then((result) => {
        if (!result.data.length) throw new Error('Nenhuma filial encontrada');
        setSubsidiaries(result.data.map((x: any) => ({ key: x.id, value: x.description })));
      });
    }
  }, [subsidiaryId]);

  useEffect(() => {
    if (open && customerId) {
      setIsLoading(true);
      customerService.getById(customerId).then(async (item: any) => {
        setCustomerData(item);
        setIsLoading(false);
        setKey((prev) => prev + 1); // Force Formik re-render when data is loaded
      }).catch(() => {
        enqueueSnackbar('Cliente não encontrado', { variant: 'error' });
        onClose();
        setIsLoading(false);
      });
    } else if (open && !customerId) {
      // Novo cliente - reset data
      setCustomerData(null);
      setIsLoading(false);
      setKey((prev) => prev + 1); // Force Formik re-render for new customer
    }
  }, [customerId, open, onClose, enqueueSnackbar]);

  const onBlurCep = (ev: any, setFieldValue: any): void => {
    setAddressByCEP(ev, setFieldValue).catch((e) => {
      enqueueSnackbar(e.message, { variant: 'warning' });
    });
  };

  const handleSubmit = async (values: Values, actions: any) => {
    try {
      if (customerId) {
        // Para UPDATE
        const currentUser = user ? { name: user.name, id: user.uid } : undefined;
        await customerService.update(values, customerId, currentUser);
        setSuccess(true);
      } else {
        // Para CREATE
        const subsidiaryIdToUse = values.subsidiary?.key || subsidiaryId || 'system';
        await customerService.create(values, subsidiaryIdToUse);
        setSuccess(true);
      }

      onSave();

      setTimeout(() => {
        onClose();
      }, 500);
    } catch (e) {
      setSuccess(false);
      enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleCheckbox = (event: any, list: any[], setFieldValue: any, fieldName: string) => {
    const optionId = parseFloat(event.target.value);
    let result = [...list];

    if (event.target.checked) {
      result.push(optionId);
    } else {
      result = result.filter((x) => x !== optionId);
    }
    setFieldValue(fieldName, result);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      fullScreen={false}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>{customerId ? 'Editar Cliente' : 'Novo Cliente'}</span>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <div className="d-flex align-items-center justify-content-center" style={{ height: '360px' }}>
            <ScaleLoader color="var(--primary)" loading />
          </div>
        ) : (
          <Formik
            key={key} // Important: force re-render when data changes
            initialValues={getInitialValues(customerData)}
            validationSchema={Yup.object().shape({
              subsidiary: Yup.lazy(() => {
                if (subsidiaryId) return Yup.object().nullable();
                return Yup.object().nullable().required();
              }),
              name: Yup.string().required(),
              businessName: Yup.string().required(),
              observations: Yup.string(),
              observationsToDriver: Yup.string(),
              email: Yup.string().email().required(),
              registryNumber: Yup.string().required(),
              responsible: Yup.string().required(),
              contract: Yup.string().required(),
              phoneNumber: Yup.string().required()
                .matches(/^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/, 'Telefone inválido'),
              categories: Yup.array().min(1, 'É necessário escolher pelo menos uma opção'),
              paymentTypes: Yup.array().min(1, 'É necessário escolher pelo menos uma opção'),
              address: Yup.object().shape({
                zipCode: Yup.string().required(),
                street: Yup.string().required(),
                neighborhood: Yup.string().required(),
                city: Yup.string().required(),
                number: Yup.string().required()
              }),
              blockedDrivers: Yup.array().of(
                Yup.object().shape({
                  id: Yup.string().required(),
                  name: Yup.string().required(),
                  email: Yup.string().email()
                })
              )
            })}
            onSubmit={handleSubmit}
            enableReinitialize // Important: reinitialize when initialValues change
          >
            {(props: FormikProps<Values>) => {
              const {
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
                setFieldValue,
                values
              } = props;

              return (
                <Form noValidate autoComplete="off">
                  <Field type="hidden" name="address.ibge" value="" />
                  <Grid container spacing={3}>
                    {subsidiaries?.length && (
                      <Grid item xs={12} sm={12} md={4}>
                        <Field
                          name="subsidiary"
                          component={Autocomplete}
                          options={subsidiaries}
                          getOptionLabel={(option: Dictionary) => option.value}
                          renderInput={(params: AutocompleteRenderInputParams) => (
                            <TextField
                              {...params}
                              fullWidth
                              required
                              variant="outlined"
                              label="Franquia"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={Boolean(errors.subsidiary && touched.subsidiary)}
                              helperText={errors.subsidiary && touched.subsidiary && errors.subsidiary}
                            />
                          )}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={subsidiaries?.length ? 4 : 8}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        label="Nome fantasia"
                        name="name"
                        autoFocus
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                        error={Boolean(errors.name && touched.name)}
                        helperText={errors.name && touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        label="Razão social"
                        name="businessName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.businessName}
                        error={Boolean(errors.businessName && touched.businessName)}
                        helperText={errors.businessName && touched.businessName && errors.businessName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={3}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        label="Nome do responsável"
                        name="responsible"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.responsible}
                        error={Boolean(errors.responsible && touched.responsible)}
                        helperText={errors.responsible && touched.responsible && errors.responsible}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        label="Número do contrato"
                        name="contract"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.contract}
                        error={Boolean(errors.contract && touched.contract)}
                        helperText={errors.contract && touched.contract && errors.contract}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        label="CNPJ/CPF"
                        name="registryNumber"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.registryNumber}
                        error={Boolean(errors.registryNumber && touched.registryNumber)}
                        helperText={errors.registryNumber && touched.registryNumber && errors.registryNumber}
                        inputProps={{ maxLength: 14 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={3}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        label="Email"
                        name="email"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        error={Boolean(errors.email && touched.email)}
                        helperText={errors.email && touched.email && errors.email}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="CEP"
                        name="address.zipCode"
                        onChange={handleChange}
                        onBlur={(e: any) => onBlurCep(e, setFieldValue)}
                        error={Boolean(errors.address?.zipCode && touched.address?.zipCode)}
                        helperText={errors.address?.zipCode && touched.address?.zipCode && errors.address?.zipCode}
                        InputProps={{ inputComponent: CepMask as any }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={5}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Endereço"
                        name="address.street"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address.street}
                        error={Boolean(errors.address?.street && touched.address?.street)}
                        helperText={errors.address?.street && touched.address?.street && errors.address?.street}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={5}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Bairro"
                        name="address.neighborhood"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address.neighborhood}
                        error={Boolean(errors.address?.neighborhood && touched.address?.neighborhood)}
                        helperText={errors.address?.neighborhood && touched.address?.neighborhood && errors.address?.neighborhood}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={5}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Cidade"
                        name="address.city"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address.city}
                        error={Boolean(errors.address?.city && touched.address?.city)}
                        helperText={errors.address?.city && touched.address?.city && errors.address?.city}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Nº"
                        name="address.number"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address.number}
                        error={Boolean(errors.address?.number && touched.address?.number)}
                        helperText={errors.address?.number && touched.address?.number && errors.address?.number}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        variant="outlined"
                        label="Complemento"
                        name="address.complement"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address.complement}
                        error={Boolean(errors.address?.complement && touched.address?.complement)}
                        helperText={errors.address?.complement && touched.address?.complement && errors.address?.complement}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        label="Telefone"
                        name="phoneNumber"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.phoneNumber}
                        error={Boolean(errors.phoneNumber && touched.phoneNumber)}
                        helperText={errors.phoneNumber && touched.phoneNumber && errors.phoneNumber}
                        InputProps={{ inputComponent: PhoneMask as any }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <FormControl required error={Boolean(errors.categories && touched.categories)} component="fieldset">
                        <FormLabel component="legend">Categorias</FormLabel>
                        <FormGroup>
                          {_.sortBy(CategoryDriverList, [(x) => x.value]).map((item: Dictionary) => (
                            <FormControlLabel
                              key={item.key}
                              value={item.key}
                              control={(
                                <Checkbox
                                  checked={values.categories.includes(item.key)}
                                  onChange={(e) => handleCheckbox(e, values.categories, setFieldValue, 'categories')}
                                  name="categories"
                                />
                              )}
                              label={item.value}
                            />
                          ))}
                        </FormGroup>
                        <FormHelperText>
                          <ErrorMessage name="categories" className="invalid-feedback" />
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <FormControl required error={Boolean(errors.paymentTypes && touched.paymentTypes)} component="fieldset">
                        <FormLabel component="legend">Tipos de pagamentos</FormLabel>
                        <FormGroup>
                          {_.sortBy(PaymentTypeList, [(x) => x.value]).map((item: Dictionary) => (
                            <FormControlLabel
                              key={item.key}
                              value={item.key}
                              control={(
                                <Checkbox
                                  checked={values.paymentTypes.includes(item.key)}
                                  onChange={(e) => handleCheckbox(e, values.paymentTypes, setFieldValue, 'paymentTypes')}
                                  name="paymentTypes"
                                />
                              )}
                              label={item.value}
                            />
                          ))}
                        </FormGroup>
                        <FormHelperText>
                          <ErrorMessage name="paymentTypes" className="invalid-feedback" />
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} style={{ display: 'none' }}>
                      <Field
                        name="blockedDrivers"
                        component={Autocomplete}
                        multiple
                        options={drivers}
                        getOptionLabel={(option: any) => option.name}
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        onInputChange={(e: any, newInputValue: React.SetStateAction<string>) => {
                          setSearchDrivers(newInputValue);
                        }}
                        renderInput={(params: AutocompleteRenderInputParams) => (
                          <TextField
                            {...params}
                            fullWidth
                            variant="outlined"
                            label="Condutores bloqueados"
                            placeholder="Digite para buscar condutores..."
                          />
                        )}
                        onChange={(event: any, newValue: any) => {
                          setFieldValue('blockedDrivers', newValue);
                        }}
                        renderTags={(value: any[], getTagProps: any) => value.map((option: any, index: any) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                        noOptionsText="Digite para buscar condutores"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Observações para o condutor"
                        name="observationsToDriver"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.observationsToDriver}
                        error={Boolean(errors.observationsToDriver && touched.observationsToDriver)}
                        helperText={errors.observationsToDriver && touched.observationsToDriver && errors.observationsToDriver}
                        multiline
                        rows="4"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Informações adicionais"
                        name="observations"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.observations}
                        error={Boolean(errors.observations && touched.observations)}
                        helperText={errors.observations && touched.observations && errors.observations}
                        multiline
                        rows="4"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container justifyContent="flex-end" spacing={3}>
                        <div className={classes.wrapper}>
                          <Fab
                            aria-label="save"
                            color="primary"
                            className={buttonClassname}
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {success ? <CheckIcon /> : <SaveIcon />}
                          </Fab>
                          {isSubmitting && <CircularProgress size={68} className={classes.fabProgress} />}
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
}
